/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useCallback, useRef, useState } from 'react';

export interface PluginLoadRequest {
  id: string;
  version?: string;
}

interface UsePluginLiveReloadReturn {
  isAlreadyLoaded: (pluginKey: string, url: string) => boolean;
  setLoadedPluginUrl: (pluginKey: string, url: string) => void;
  loadedPluginRequests: Record<string, PluginLoadRequest>;
  registerRequest: (pluginKey: string, id: string, version?: string) => void;
  unregisterRequestsForPlugin: (pluginId: string) => void;
}

export const usePluginLiveReload = (): UsePluginLiveReloadReturn => {
  const loadedPluginUrlsRef = useRef<Record<string, string>>({});

  const [loadedPluginRequests, setLoadedPluginRequests] = useState<
    Record<string, PluginLoadRequest>
  >({});

  const isAlreadyLoaded = useCallback(
    (pluginKey: string, url: string) =>
      loadedPluginUrlsRef.current[pluginKey] === url,
    [],
  );

  const registerRequest = useCallback(
    (pluginKey: string, id: string, version?: string) => {
      setLoadedPluginRequests((prev) => {
        const current = prev[pluginKey];
        if (current?.id === id && current?.version === version) {
          return prev;
        }
        return { ...prev, [pluginKey]: { id, version } };
      });
    },
    [],
  );

  const setLoadedPluginUrl = useCallback((pluginKey: string, url: string) => {
    loadedPluginUrlsRef.current = {
      ...loadedPluginUrlsRef.current,
      [pluginKey]: url,
    };
  }, []);

  const unregisterRequestsForPlugin = useCallback((pluginId: string) => {
    setLoadedPluginRequests((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([, request]) => request.id !== pluginId),
      ),
    );
  }, []);

  return {
    isAlreadyLoaded,
    setLoadedPluginUrl,
    loadedPluginRequests,
    registerRequest,
    unregisterRequestsForPlugin,
  };
};
